import { compareISO, parseMonth, toISODate } from '@backrs_ui/common-ui/src/utils/dateTime.util';

import { eachDayOfInterval, endOfMonth, endOfWeek, isSameMonth, startOfWeek } from 'date-fns';

import { CalendarDay, DayStatus } from './monthCalendar.types';

export type GridCell = Readonly<{
  iso: string;
  dayOfMonth: number;
  /** false for the leading/trailing days borrowed from the adjacent months */
  inCurrentMonth: boolean;
}>;

/**
 * Full weeks (Mon–Sun) covering `month`, with leading/trailing days from the
 * adjacent months so the grid is always complete rectangles.
 */
export function getMonthGrid(month: string): GridCell[] {
  const { year, monthIndex } = parseMonth(month);
  const firstOfMonth = new Date(year, monthIndex, 1);
  const weekStartsOn = 1; // Monday-first grid

  return eachDayOfInterval({
    start: startOfWeek(firstOfMonth, { weekStartsOn }),
    end: endOfWeek(endOfMonth(firstOfMonth), { weekStartsOn }),
  }).map((date) => ({
    iso: toISODate(date),
    dayOfMonth: date.getDate(),
    inCurrentMonth: isSameMonth(date, firstOfMonth),
  }));
}

/**
 * The one client-side derivation this component is allowed to make: visual status
 * from `today` + the day's `completed` flag. Non-actionable days are never
 * "due"/"overdue" — they carry no task to miss.
 */
export function deriveStatus(day: CalendarDay, today: string): DayStatus {
  if (day.completed) return 'completed';
  if (!day.actionable) return 'upcoming';

  const order = compareISO(day.date, today);

  if (order < 0) return 'overdue';
  if (order === 0) return 'due';

  return 'upcoming';
}
